import { CloseCircleOutlined } from '@ant-design/icons';
import { ExportButton, IconButton } from '@monorepo/shared-lib';
import { Alert, Divider, Popover, Radio, Space, Tooltip } from 'antd';
import { ReactComponent as CopyIcon } from 'assets/images/icons/copyIcon.svg';
import { ReactComponent as EyeIcon } from 'assets/images/icons/eyeIcon.svg';
import { ReactComponent as HmrcIcon } from 'assets/images/icons/hmrcIcon.svg';
import { ReactComponent as IconExport } from 'assets/images/icons/iconExport.svg';
import { StyledCard } from 'components/Custom/card';
import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { DataLogStatus, TaxReportCountry } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { User } from 'store/auth/models';
import { VatReportData } from 'store/vat/models';
import { getExcelLoadingState } from 'store/vat/selectors';
import { useNumberFormat } from 'utils/numberFormat';
import { CyprusForm } from './forms/cyprus';
import { IrelandForm } from './forms/ireland';
import { IsleOfManForm } from './forms/isleOfMan';
import { JerseyForm } from './forms/jersey';
import { MaltaForm } from './forms/malta';
import { NetherlandsForm } from './forms/netherlands';
import { SouthAfricaForm } from './forms/southAfrica';
import { UAEForm } from './forms/uae';
import { UKForm } from './forms/uk';
import { ReportCssWrapper } from './report-css-wrapper';
import { OSSForm } from './forms/oss';
import { MappingTaxRate } from 'store/returns/models/mappingTaxRate';
import { Section } from 'store/returns/models/returnData';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
interface VatReturnFormsProps {
  taxReportCountry: TaxReportCountry;
  data?: VatReportData;
  resetValues: boolean;
  resetFinished: () => void;
  setActiveKey: (key) => void;
  setFilteredBox: (key) => void;
  renderClientBox: () => React.ReactNode;
  renderContextMenu: (onClose?: () => void) => React.ReactNode;
  currentUser: User;
  isAdjustedReturn?: boolean;
  switchTotals: boolean;
  setSwitchTotals: (value: boolean) => void;
  reportId: string;
  mappingId: string;
  exporting: boolean;
  comparedData?: VatReportData | null;
  clearComparison?: () => void;
  canSubmitToHmrc?: boolean;
  onSubmitToHmrc?: () => void;
  jsonMapping?: MappingTaxRate[];
  mappingSections?: Section[];
  reportingTypeId?: string;
  status?: string | null;
  /** When false, box values do not open the transactions tab. */
  showBoxTransactions?: boolean;
}

export const VatReturnForms: React.FC<VatReturnFormsProps> = ({
  taxReportCountry,
  data,
  resetValues,
  resetFinished,
  setActiveKey,
  setFilteredBox,
  renderClientBox,
  renderContextMenu,
  currentUser,
  isAdjustedReturn,
  switchTotals,
  setSwitchTotals,
  exporting,
  comparedData,
  clearComparison,
  canSubmitToHmrc = false,
  onSubmitToHmrc,
  jsonMapping,
  mappingSections,
  reportingTypeId,
  status,
  showBoxTransactions = true,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { formatNumber } = useNumberFormat();
  const formatNumberRef = React.useRef(formatNumber);
  formatNumberRef.current = formatNumber;
  const excelLoadingState = useSelector(getExcelLoadingState);
  const [contextMenuVisible, setContextMenuVisible] = React.useState(false);
  const pwaMobileNav = usePwaMobileNavEnabled();

  React.useEffect(() => {
    if (resetValues) {
      const valueFields = document.querySelectorAll('.value-field-span');
      valueFields.forEach((valueField) => {
        valueField.textContent = '0.00';
      });
      resetFinished();
    }
  }, [resetValues]);

  const copyCurrentValue = (boxNumber: string) => {
    const subsectionValueSpan = document.getElementById(
      `subsection${boxNumber}-value-span`
    );
    if (subsectionValueSpan) {
      // Copy raw value (no grouping) when set by form, so paste gives e.g. "1234.56"
      const raw = subsectionValueSpan.getAttribute('data-raw-value');
      let toCopy =
        raw != null && raw !== ''
          ? raw
          : (subsectionValueSpan.textContent ?? '').trim();
      if (toCopy !== '') {
        const isCyprus =
          (taxReportCountry ?? '').toString().trim().toLowerCase() ===
          TaxReportCountry.CYPRUS.toLowerCase();
        // Cyprus Taxisnet expects comma as the decimal separator, e.g. "1234,56"
        if (isCyprus && raw != null && raw !== '') {
          toCopy = raw.replace('.', ',');
        }
        navigator.clipboard.writeText(toCopy);
      }
    }

    const message: MessageData = {
      title: '',
      description: t('common.copied-to-clipboard'),
      type: MessageStates.INFO,
    };
    dispatch(turnMessageOn(message));
  };

  const showTransactionByBox = React.useCallback((boxNumber: string) => {
    setActiveKey('2');
    const subsectionBoxNameCell = document.getElementById(
      `subsection${boxNumber}-box-name`
    );
    setFilteredBox(subsectionBoxNameCell?.textContent);
  }, []);

  const getComparedValue = (boxNumber: string): number | null => {
    if (!comparedData?.subsections) return null;
    const subsection = comparedData.subsections.find((sub) => {
      const code = sub.code?.replace(/^Box\s+/, '').toLowerCase();
      return code === boxNumber.toLowerCase();
    });
    return subsection?.net ?? null;
  };

  // Update comparison values and arrows after data is loaded
  React.useEffect(() => {
    if (!comparedData || !data || !comparedData.subsections) {
      // Clear comparison if no compared data
      document
        .querySelectorAll('[id$="-compared-value"]')
        .forEach((el) => el.remove());
      document
        .querySelectorAll('[id$="-arrow-container"]')
        .forEach((el) => el.remove());
      return;
    }

    // Safety check: ensure data and comparedData are different objects
    if (data === comparedData) {
      return;
    }

    // Wait for form's useEffect to finish updating the DOM
    const timeoutId = setTimeout(() => {
      const fmt = formatNumberRef.current;
      // Get current subsections from data prop (use adjustedSubsections if switchTotals is true)
      const currentSubsections = switchTotals
        ? data?.adjustedSubsections
        : data?.subsections;

      comparedData.subsections?.forEach((comparedSubsection) => {
        // Extract box number - handle different formats
        let boxCode = comparedSubsection.code?.replace(/^Box\s+/, '');
        if (!boxCode) return;

        // Try both with and without lowercase for compatibility
        const boxCodeLower = boxCode.toLowerCase();
        const boxCodeOriginal = boxCode;

        // Try to find the value span element (try both formats) for DOM insertion
        let currentElement = document.getElementById(
          `subsection${boxCodeOriginal}-value-span`
        );
        let actualBoxCode = boxCodeOriginal;
        if (!currentElement) {
          currentElement = document.getElementById(
            `subsection${boxCodeLower}-value-span`
          );
          if (currentElement) {
            actualBoxCode = boxCodeLower;
          }
        }

        if (!currentElement) return;

        // Get current value from DOM (which is updated by form's useEffect with current return data)
        // This is more reliable than reading from data prop which might not have updated yet
        const currentText = currentElement.textContent || '0.00';
        const currentValue = parseFloat(currentText.replace(/,/g, '')) || 0;
        const comparedValue = comparedSubsection.net ?? 0;

        // Also try to get from data prop as fallback/verification
        const currentSubsection = currentSubsections?.find((sub) => {
          const subCode = sub.code?.replace(/^Box\s+/, '');
          return (
            subCode?.toLowerCase() === boxCodeLower ||
            subCode === boxCodeOriginal
          );
        });
        const currentValueFromData = currentSubsection?.net ?? 0;

        // Find the parent container - it should be the td element
        let valueContainer = currentElement.parentElement;
        // If parent is a div, go up one more level
        if (valueContainer && valueContainer.tagName === 'DIV') {
          valueContainer = valueContainer.parentElement;
        }
        if (!valueContainer) return;

        // Check if comparison value already exists, remove it first
        const existingCompared = document.getElementById(
          `subsection${actualBoxCode}-compared-value`
        );
        if (existingCompared) {
          existingCompared.remove();
        }
        const existingArrow = document.getElementById(
          `subsection${actualBoxCode}-arrow-container`
        );
        if (existingArrow) {
          existingArrow.remove();
        }

        // Create comparison value element
        const comparedValueElement = document.createElement('p');
        comparedValueElement.id = `subsection${actualBoxCode}-compared-value`;
        comparedValueElement.style.cssText =
          'font-size: 12px; color: var(--color-text-muted, #5a6a6a); margin: 2px 0 0 0; font-weight: 400; text-align: right;';

        // Display comparison value (from previous return)
        comparedValueElement.textContent = fmt(comparedValue);

        // Calculate difference: old return - new return (comparedValue - currentValue)
        // Positive = value decreased (red arrow), Negative = value increased (green arrow)
        const difference = comparedValue - currentValue;

        // Create arrow container
        const arrowContainer = document.createElement('div');
        arrowContainer.id = `subsection${actualBoxCode}-arrow-container`;
        arrowContainer.style.cssText =
          'margin-top: 2px; display: flex; align-items: center; justify-content: flex-end;';

        // Update arrow based on difference - show actual difference value
        if (Math.abs(difference) > 0.01) {
          // Positive difference = old > new = value decreased = red arrow
          // Negative difference = old < new = value increased = green arrow
          const arrowColor = difference > 0 ? '#ff4d4f' : '#52c41a';
          const arrowSymbol = difference > 0 ? '↓' : '↑';
          const differenceText = fmt(Math.abs(difference));
          arrowContainer.innerHTML = `<span style="color: ${arrowColor}; font-size: 10px; margin-left: 4px; font-weight: 500;">${arrowSymbol} ${differenceText}</span>`;
        }

        // Insert comparison elements after the value span's parent
        const valueSpanParent = currentElement.parentElement;
        if (valueSpanParent) {
          valueSpanParent.appendChild(comparedValueElement);
          if (arrowContainer.innerHTML) {
            valueSpanParent.appendChild(arrowContainer);
          }
        }
      });
    }, 800); // Increased timeout to ensure form's useEffect has finished

    return () => clearTimeout(timeoutId);
  }, [data, comparedData, switchTotals]);

  const renderValueCell = (boxNumber: string, withP = false) => {
    const id = withP
      ? `subsection${boxNumber}-value-p-span`
      : `subsection${boxNumber}-value-span`;

    return (
      <>
        <p id={id} className="value-field-span">
          0.00
        </p>
        {pwaMobileNav ? null : (
        <Space className="show-on-hover">
          {showBoxTransactions && (
            <Tooltip title={t('vat-report.view-transactions-make-up-value')}>
              <IconButton
                type="default"
                className="icon-button-hover"
                aria-label={t('vat-report.view-transactions-make-up-value')}
                style={{
                  height: '30px',
                  borderRadius: 50,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '14px 10px',
                }}
                icon={
                  <EyeIcon
                    className="hover-icon"
                    width={'23px'}
                    height={'23px'}
                  />
                }
                onClick={() => showTransactionByBox(boxNumber)}
              ></IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('vat-report.copy-value-to-clipboard')}>
            <IconButton
              type="default"
              className="icon-button-hover"
              aria-label={t('vat-report.copy-value-to-clipboard')}
              style={{
                height: '30px',
                borderRadius: 50,
                display: 'flex',
                alignItems: 'center',
                padding: '14px 10px',
              }}
              icon={
                <CopyIcon
                  className="hover-icon"
                  width={'16px'}
                  height={'16px'}
                />
              }
              onClick={() => copyCurrentValue(boxNumber)}
            ></IconButton>
          </Tooltip>
        </Space>
        )}
      </>
    );
  };

  const formRootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!pwaMobileNav) return undefined;
    const root = formRootRef.current;
    if (!root) return undefined;
    const openRow = (row: HTMLElement) => {
      const boxCell = row.querySelector<HTMLElement>('[id$="-box-name"]');
      const boxNumber = boxCell?.id.match(/^subsection(.+)-box-name$/)?.[1];
      if (!boxNumber) return;
      showTransactionByBox(boxNumber);
    };
    root.querySelectorAll('tr').forEach((row) => {
      if (!row.querySelector('[id$="-box-name"]')) return;
      row.setAttribute('tabindex', '0');
      row.setAttribute('role', 'button');
    });
    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.closest('button, a')) return;
      const row = target.closest('tr');
      if (!row || !root.contains(row)) return;
      openRow(row);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = event.target as HTMLElement | null;
      const row = target?.closest('tr');
      if (!row || !root.contains(row) || row !== target) return;
      event.preventDefault();
      openRow(row);
    };
    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeyDown);
    return () => {
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeyDown);
    };
  }, [pwaMobileNav, data, switchTotals, showTransactionByBox]);

  // UK check for form and UK-only actions (e.g. Submit to HMRC)
  const country = (taxReportCountry ?? '').toString().trim().toLowerCase();
  const isUK =
    country === TaxReportCountry.UK.toLowerCase() ||
    country === 'united kingdom' ||
    country === 'uk';
  // Keep implementation, but hide this standalone header button.
  const showTopHmrcSubmitButton = false;
  const isSouthAfrica =
    country === TaxReportCountry.SOUTH_AFRICA.toLowerCase() ||
    country === 'south africa';

  const renderForm = () => {
    if (isUK) {
      return (
        <ReportCssWrapper>
          <UKForm
            data={data}
            renderValueCell={renderValueCell}
            finalTotals={switchTotals}
          />
        </ReportCssWrapper>
      );
    }
    if (isSouthAfrica) {
      return (
        <ReportCssWrapper>
          <SouthAfricaForm
            data={data}
            renderValueCell={renderValueCell}
            finalTotals={switchTotals}
          />
        </ReportCssWrapper>
      );
    }

    switch (taxReportCountry) {
      case TaxReportCountry.MALTA:
        return (
          <ReportCssWrapper>
            <MaltaForm
              data={data}
              renderValueCell={renderValueCell}
              finalTotals={switchTotals}
            />
          </ReportCssWrapper>
        );
      case TaxReportCountry.NETHERLANDS:
        return (
          <ReportCssWrapper>
            <NetherlandsForm
              data={data}
              renderValueCell={renderValueCell}
              finalTotals={switchTotals}
            />
          </ReportCssWrapper>
        );
      case TaxReportCountry.CYPRUS:
        return (
          <ReportCssWrapper>
            <CyprusForm
              data={data}
              renderValueCell={renderValueCell}
              finalTotals={switchTotals}
            />
          </ReportCssWrapper>
        );
      case TaxReportCountry.JERSEY:
        return (
          <ReportCssWrapper>
            <JerseyForm
              data={data}
              renderValueCell={renderValueCell}
              finalTotals={switchTotals}
            />
          </ReportCssWrapper>
        );
      case TaxReportCountry.UAE:
        return (
          <ReportCssWrapper>
            <UAEForm
              data={data}
              renderValueCell={renderValueCell}
              finalTotals={switchTotals}
            />
          </ReportCssWrapper>
        );
      case TaxReportCountry.ISLE_OF_MAN:
        return (
          <ReportCssWrapper>
            <IsleOfManForm
              data={data}
              renderValueCell={renderValueCell}
              finalTotals={switchTotals}
            />
          </ReportCssWrapper>
        );
      case TaxReportCountry.IRELAND_DETAILED:
      case TaxReportCountry.IRELAND_STANDARD:
      case TaxReportCountry.IRELAND_RTD:
        return (
          <ReportCssWrapper>
            <IrelandForm
              data={data}
              renderValueCell={renderValueCell}
              taxReportCountry={taxReportCountry}
              finalTotals={switchTotals}
            />
          </ReportCssWrapper>
        );
      case TaxReportCountry.EUROPE:
        return (
          <ReportCssWrapper>
            <OSSForm
              data={data}
              renderValueCell={renderValueCell}
              finalTotals={switchTotals}
              jsonMapping={jsonMapping}
              mappingSections={mappingSections}
              returnTypeId={reportingTypeId}
            />
          </ReportCssWrapper>
        );
      default:
        return (
          <Alert
            message={t('common.error')}
            description={t('vat-report.no-return-forms-found-for-country', {
              country: taxReportCountry,
            })}
            type="error"
            showIcon
          />
        );
    }
  };
  const renderCardTitle = () => {
    const isSubmitted =
      !!status && DataLogStatus[status] === DataLogStatus.SUBMITTED;
    // Submitted returns do not load transactions; export uses totals only.
    const exportDisabled =
      !data ||
      (!isSubmitted && !(data.transactionsSize > 0 || data.exceptionsSize > 0));

    const showHmrcSubmit = isUK && showTopHmrcSubmitButton;
    const showResetCompare = !!(comparedData && clearComparison);
    const showExportVatReturn = !pwaMobileNav;
    const showToolbar =
      isAdjustedReturn ||
      showExportVatReturn ||
      showHmrcSubmit ||
      showResetCompare;

    if (!showToolbar) {
      return null;
    }

    return (
      <>
        <div
          className={
            pwaMobileNav ? 'scope-pwa-vat-return-form-toolbar' : undefined
          }
        >
          <Space
            align={pwaMobileNav ? 'stretch' : 'center'}
            direction={pwaMobileNav ? 'vertical' : 'horizontal'}
            size={pwaMobileNav ? 10 : 8}
            style={{
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Space
              align={pwaMobileNav ? 'stretch' : 'center'}
              direction={pwaMobileNav ? 'vertical' : 'horizontal'}
              size={pwaMobileNav ? 10 : 8}
              style={{ width: pwaMobileNav ? '100%' : undefined }}
            >
              {isAdjustedReturn && !pwaMobileNav && (
                <Radio.Group
                  defaultValue={switchTotals}
                  buttonStyle="solid"
                  style={{
                    borderRadius: '10px',
                    width: pwaMobileNav ? '100%' : undefined,
                  }}
                  onChange={() => setSwitchTotals(!switchTotals)}
                >
                  <Radio.Button value={false}>
                    {t('vat-report.original')}
                  </Radio.Button>
                  <Radio.Button value={true}>
                    {t('vat-report.final-includes-adjustments')}
                  </Radio.Button>
                </Radio.Group>
              )}
              {showExportVatReturn && (
                <Popover
                  placement="topLeft"
                  arrow={false}
                  trigger="click"
                  content={renderContextMenu(() => setContextMenuVisible(false))}
                  title={''}
                  overlayStyle={{ zIndex: 999 }}
                  open={contextMenuVisible}
                  onOpenChange={setContextMenuVisible}
                  destroyOnHidden={true}
                >
                  <ExportButton
                    icon={<IconExport style={{ width: 20, height: 20 }} />}
                    loading={excelLoadingState || exporting}
                    disabled={exportDisabled}
                  >
                    <span>{t('vat-report.export-vat-return')}</span>
                  </ExportButton>
                </Popover>
              )}
            </Space>
            <Space align="center">
            {showHmrcSubmit && (
              <Tooltip title={t('vat-report.hmrc-submit-tooltip')}>
                <span
                  style={{
                    opacity: canSubmitToHmrc ? 1 : 0.6,
                    display: 'inline-block',
                    cursor: canSubmitToHmrc ? 'pointer' : 'not-allowed',
                  }}
                >
                  <ExportButton
                    disabled={!canSubmitToHmrc}
                    onClick={onSubmitToHmrc}
                    style={{
                      cursor: canSubmitToHmrc ? 'pointer' : 'not-allowed',
                    }}
                    icon={<HmrcIcon style={{ width: 20, height: 20 }} />}
                  >
                    <span>{t('vat-report.submit-to-hmrc')}</span>
                  </ExportButton>
                </span>
              </Tooltip>
            )}
            {showResetCompare && (
              <Tooltip title={t('vat-report.reset-compare-tooltip')}>
                <ExportButton
                  onClick={clearComparison}
                  icon={<CloseCircleOutlined />}
                >
                  {t('vat-report.reset-compare')}
                </ExportButton>
              </Tooltip>
            )}
          </Space>
          </Space>
        </div>
        {!pwaMobileNav ? <Divider /> : null}
      </>
    );
  };
  return (
    <StyledCard //@ts-ignore
      className={pwaMobileNav ? 'scope-pwa-vat-return-form' : undefined}
      style={{
        maxWidth: 1150,
        minWidth: pwaMobileNav ? 0 : 780,
        margin: 'auto',
        width: '100%',
      }}
    >
      <div
        ref={formRootRef}
        style={{
          width: '100%',
          maxWidth: pwaMobileNav ? '100%' : '1100px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <div className="form-wrapper">
          {renderClientBox()}
          <div
            style={{
              width: '100%',
            }}
          >
            {renderCardTitle()}
          </div>
          <div
            id="vat-to-be-exported"
            className={
              pwaMobileNav ? 'scope-pwa-vat-return-table-scroll' : undefined
            }
          >
            {renderForm()}
          </div>
        </div>
      </div>
    </StyledCard>
  );
};
